import { getGlobalLightHub, getAsyncQueueId, runInQueue, timeout } from "./utils";
import { SnapShot, Attr } from "./typing";
import { getAttrs } from "./dom";
import { GLOBAL_KEY, INTERNAL_LIGHTHUB_KEEP_ONCE, INTERNAL_DATA_URL } from "./const";
import { logger } from "./logger";
import { startTiming, endTiming, pointTiming } from "./timing";

/**
 * 把页面状态重置到页面初始状态
 */
export async function resetSandbox() {
    
    const lighthub = getGlobalLightHub();
    await applySnapshot({
        window: getGlobalLightHub().initalWindow,
        headChildren: [],
        bodyAttrs: [],
        bodyChildren: [],
        windowListeners: [],
        documentListeners: [],
        timeoutTimers: [],
        intervalTimers: [],
        mutationObservers: [],
        htmlAttrs: [],
        requestIdleCallbackListeners: [],
        requestAnimationFrameListeners: [],
        prototypes: lighthub.initalPrototypes,
    });
}


let deferScriptTaskQueue = [] as Array<() => Promise<void>>;
let scriptCount = 1;

document.addEventListener('lighthubbeforenavigation', () => {
    deferScriptTaskQueue = [];
    scriptCount = 1;
});

document.addEventListener('lighthubbeforeback', () => {
    deferScriptTaskQueue = [];
    scriptCount = 1;

});

async function bootstrapScriptTaskQueue() {
    
    try {
        for (let item of deferScriptTaskQueue) {
            await runInQueue(() => item());
        }
    } catch (e) {
    }

    deferScriptTaskQueue = [];
}

function isJSONP(script: HTMLElement) {
    const _isJSONP = getGlobalLightHub().hooks.isJSONP;

    if (!_isJSONP) {
        return false;
    } else {
        return _isJSONP(script);
    }
}

var a: any = [];


async function applyScript(script: HTMLScriptElement, container: HTMLElement) {
    if (isJSONP(script)) {
        return;
    }

    const lighthub = getGlobalLightHub();
    const src = (script.getAttribute('src') || '').replace(/^http(s):/, '');
    if (script.type && script.type !== 'text/javascript') {
        // custom script type
        container.appendChild(script);
        return;
    }
    const count = scriptCount ++;

    if (!src) {
        // inline script

        let code = script.innerHTML || '';
        const tag = document.createElement('script');

        tag.appendChild(document.createTextNode(code));
        return new Promise(r => {
            pointTiming('script-start-' + count);
            container.appendChild(tag);

            r(timeout(0));
        });
    } else {

        if (lighthub.globalIgnoreScript.indexOf(src) !== -1) {
            return;
        }
        const loadScript = () => new Promise((resolve) => {
            
            const tag = document.createElement('script');
            tag.src = src;

            if (typeof script.getAttributeNames === 'function') {
                const names = script.getAttributeNames();
                for (let i = 0; i < names.length; i ++) {
                    const name = names[i];
                    tag.setAttribute(name, script.getAttribute(name) || '');
                }
            }

            tag.onload = () => {
                resolve();
            };

            tag.onerror = (e) => {
                console.error(e);
                resolve();
            };
            // document.head.appendChild(tag);
            container.appendChild(tag);
        }) as Promise<void>;


        if (script.hasAttribute('defer') && script.getAttribute('defer') !== 'false') {
            deferScriptTaskQueue.push(loadScript);
        } else if (script.hasAttribute('async') && script.getAttribute('async') !== 'false') {
            startTiming('load-script-' + count);
            pointTiming('script-start-' + count);

            
            loadScript().then(() => {
                endTiming('load-script-' + count);
            });
        } else {
            pointTiming('script-start-' + count);

            startTiming('load-script-' + count);
            await loadScript();
            startTiming('load-script-' + count);
        }
    }
}


function reapplyJSONP(head: Element) {
    const lighthub = getGlobalLightHub();

    head.querySelectorAll('script').forEach(tag => {
        if (isJSONP(tag)) {
            const newTag = document.createElement('script');
            newTag.src = tag.src;
            newTag.async = true;
            tag.remove();
            head.appendChild(newTag);
        }
    });
}

function applyWindow(windowSnapshot: any, prototypeSnapshot: any) {
    const lighthub = getGlobalLightHub();

    const skipKey = (key: string) => {
        return key === GLOBAL_KEY || key === 'scrollY' || key === 'XMLHttpRequest' || key === '__coverage__' || key === 'Promise';
    }


    // prototypes
    for (let key of Object.keys(prototypeSnapshot)) {
        for (let prop of Object.keys(prototypeSnapshot[key])) {
            try {
                // @ts-ignore
                if (window[key].prototype[prop] !== prototypeSnapshot[key][prop]) {
                    // @ts-ignore
                    window[key].prototype[prop] = prototypeSnapshot[key][prop];
                }
            } catch(e){}
        }
    }

    for (let k of Object.keys(window)) {
        if (skipKey(k)) {
            continue;
        }


        if (windowSnapshot.hasOwnProperty(k)) {
            // @ts-ignore
            if (window[k] !== windowSnapshot[k]) {
                // @ts-ignore
                window[k] = windowSnapshot[k];
            }
        } else {
            if (lighthub.globalExposedKey && lighthub.globalExposedKey.find(item => {
                if (typeof item === 'string') {
                    return item === k;
                } else {
                    return item.test(k);
                }
            }) ) {
                // keep it
            } else {
                try {
                    // @ts-ignore
                    delete window[k];
                } catch(e){}
            }
        }
    }

    for (let k of Object.keys(windowSnapshot)) {
        if (skipKey(k)) {
            continue;
        }
        // @ts-ignore
        if (window[k] !== windowSnapshot[k]) {
            // @ts-ignore
            window[k] = windowSnapshot[k];
        }
    }

    

    const {
        scrollX,
        scrollY,
    } = windowSnapshot;

    return () => {
        

        getGlobalLightHub().origins._setTimeout.call(window, () => {
            window.scroll(scrollX, scrollY);
        }, 0);
    };
}



export function applyDeferScriptInPage() {
    return bootstrapScriptTaskQueue();
}

/**
 * 把页面当前状态快照存为一个对象
 */
export function snapshotAll(): SnapShot {
    const snapshotWindow = {} as any;
    for (let k of Object.keys(window)) {
        if (k === GLOBAL_KEY) {
            continue;
        }
        // @ts-ignore
        const v = window[k];
        snapshotWindow[k] = v;
    }
    const lighthub = getGlobalLightHub();

    const prototypes = {} as {[key: string]: any};

    for (let key of ['HTMLElement', 'Element']) {
        // @ts-ignore
        if (!window[key]) continue;
        prototypes[key] = {};
        // @ts-ignore
        for (let prop in window[key].prototype) {
            try {
                // @ts-ignore
                prototypes[key][prop] = window[key].prototype[prop];
            } catch(e){}
        }
    }

    const r = {
        window: snapshotWindow,
        headChildren: Array.from(document.head.children),
        bodyChildren: Array.from(document.body.children),
        bodyAttrs: getAttrs(document.body).slice(0),
        htmlAttrs: getAttrs(document.documentElement).slice(0),
        windowListeners: lighthub.windowListeners.slice(0),
        documentListeners: lighthub.documentListeners.slice(0),
        intervalTimers: lighthub.intervalTimers.slice(0),
        timeoutTimers: lighthub.timeoutTimers.slice(0),
        mutationObservers: lighthub.mutationObservers && lighthub.mutationObservers.slice(0),
        requestAnimationFrameListeners: lighthub.requestAnimationFrameListeners && lighthub.requestAnimationFrameListeners.slice(0),
        requestIdleCallbackListeners: lighthub.requestIdleCallbackListeners && lighthub.requestIdleCallbackListeners.slice(0),
        prototypes,
    }

    r.headChildren.forEach(item => applyStyleSheetInDOM(item as HTMLElement, r.headChildren));
    r.bodyChildren.forEach(item => applyStyleSheetInDOM(item as HTMLElement, r.headChildren));
    return r;

}


function applyAttrs(ele: Element, attrs: Attr[]) {
    ele.getAttributeNames().forEach(name => ele.removeAttribute(name));
    attrs.forEach(attr => {
        ele.setAttribute(attr.key, attr.val);
    });
}

async function applyStylesheet(item: HTMLLinkElement, container: HTMLElement | Array<any>) {
    const styleItem = item as HTMLLinkElement;
    const href = styleItem.getAttribute('href');
    // 
    if (href) {

        const res = await runInQueue(() => getGlobalLightHub().origins._fetch.call(window, href));
        const text = await runInQueue(() => res.text());
        const tag = document.createElement('style');
        tag.innerHTML = text;
        tag.setAttribute(INTERNAL_DATA_URL, href);

        if (Array.isArray(container)) {
            container.push(tag);
        } else {
            container.appendChild(tag);
        }
    }
}

async function applyDOM(item: HTMLElement, container: HTMLElement, isBack: boolean) {
    const tagName = item.tagName.toUpperCase();
    // 

    if (tagName === 'SCRIPT') {
        if (isBack) {
            return;
        }
        await runInQueue(() => applyScript(item as HTMLScriptElement, container));
        if (isBack) {
            // @ts-ignore
            if (item.src) {
                // @ts-ignore
                item.setAttribute('data-lighthub-src', item.src);
                item.removeAttribute('src');
            }

            container.appendChild(item);
        }


    // @ts-ignore
    } else if (tagName === 'LINK' && item.rel === 'stylesheet') {
        // CSS

        // ignore
    } else {
        const scripts = Array.from(item.querySelectorAll('script'));
        for (let s of scripts) {
            // @ts-ignore
            s.__parentElement = s.parentElement;

            s.parentElement && s.parentElement.removeChild(s);
        }
        container.appendChild(item);

        if (!isBack) {
            for (let s of scripts) {
                await runInQueue(() => timeout(0));
                // @ts-ignore
                await runInQueue(() => applyScript(s, s.__parentElement || container));
            }
        }

    }
}

async function applyStyleSheetInDOM(dom: HTMLElement, container: HTMLElement | Array<any>) {
    const alltask = [];
    dom.querySelectorAll('link[rel="stylesheet"]').forEach(item => {
        alltask.push(applyStylesheet(item as HTMLLinkElement, container));
    });

    // @ts-ignore
    if (dom.tagName.toUpperCase() === 'LINK' && dom.rel === 'stylesheet') {
        alltask.push(applyStylesheet(dom as HTMLLinkElement, container));
    }
    return Promise.all(alltask);
}


export async function applySnapshot(snapshot: SnapShot, isBack: boolean = false, noClean = false, showBody?: () => void) {
    
    
    const lighthub = getGlobalLightHub();
    const {
        _documentRemoveEventListener,
        _windowRemoveEventListener,
        _clearInterval,
        _clearTimeout,
        _windowAddEventListener,
        _documentAddEventListener,
        _setTimeout,
        _setInterval,
        _mutationObserverObserve,
        _mutationObserverDisconnect,
        _requestAnimationFrame,
        _cancelAnimationFrame,
        _requestIdleCallback,
        _cancelIdleCallback,
    } = lighthub.origins;
    if (!noClean) {
        lighthub.documentListeners.forEach(listener => {
            // @ts-ignore
            _documentRemoveEventListener(listener.key, listener.func);
        });
        lighthub.documentListeners = [];

        lighthub.windowListeners.forEach(listener => {
            // @ts-ignore
            _windowRemoveEventListener(listener.key, listener.func);
        });
        lighthub.windowListeners = [];

        lighthub.intervalTimers.forEach(item => {
            _clearInterval(item.timer);
        });
        lighthub.intervalTimers = [];

        lighthub.timeoutTimers.forEach(item => {
            _clearTimeout(item.timer);
        });
        lighthub.timeoutTimers = [];

        lighthub.mutationObservers && lighthub.mutationObservers.forEach(item => {
            _mutationObserverDisconnect.call(item.observer);
        });
        lighthub.mutationObservers = [];

        lighthub.requestAnimationFrameListeners && lighthub.requestAnimationFrameListeners.forEach(item => {
            // @ts-ignore
            _cancelAnimationFrame.call(window, item.timer);
        });
        lighthub.requestAnimationFrameListeners = [];

        lighthub.requestIdleCallbackListeners && lighthub.requestIdleCallbackListeners.forEach(item => {
            // @ts-ignore
            _cancelIdleCallback.call(window, item.timer);
        });
        lighthub.requestIdleCallbackListeners = [];

        const keepInHTML = isBack ? [] :
        Array.from(document.body.querySelectorAll(`[${INTERNAL_LIGHTHUB_KEEP_ONCE}]`));

        document.body.innerHTML = '';

        keepInHTML.forEach(item => {
            item.removeAttribute(INTERNAL_LIGHTHUB_KEEP_ONCE);

            document.body.appendChild(item);
        });

        Array.from(document.body.children).forEach(item => {
            if (item.hasAttribute(INTERNAL_LIGHTHUB_KEEP_ONCE)) {
                item.removeAttribute(INTERNAL_LIGHTHUB_KEEP_ONCE);
            } else {
                if (!isJSONP(item as HTMLScriptElement)) {
                    item.remove();
                }
            }
        });
    }

    if (!noClean) {
        Array.from(document.head.children).forEach(item => {
            if (!isJSONP(item as HTMLScriptElement)) {
                item.remove();
            }
        });
    }



    if (snapshot) {
        // console.log('apply snapshot', snapshot);
        // apply body

        const done = applyWindow(snapshot.window, snapshot.prototypes);


        snapshot.windowListeners.forEach(item => {
            // @ts-ignore
            _windowAddEventListener(item.key, item.func);
        });
        lighthub.windowListeners = snapshot.windowListeners;

        snapshot.documentListeners.forEach(item => {
            // @ts-ignore
            _documentAddEventListener(item.key, item.func);
        });
        lighthub.documentListeners = snapshot.documentListeners;

        snapshot.timeoutTimers.forEach(item => _setTimeout(item.cb, item.ms));
        lighthub.timeoutTimers = snapshot.timeoutTimers;

        snapshot.intervalTimers.forEach(item => _setInterval(item.cb, item.ms));
        lighthub.intervalTimers = snapshot.intervalTimers;

        // @ts-ignore
        snapshot.requestAnimationFrameListeners && snapshot.requestAnimationFrameListeners.forEach(item => _requestAnimationFrame(item.cb));
        lighthub.requestAnimationFrameListeners = snapshot.requestAnimationFrameListeners;

        // @ts-ignore
        snapshot.requestIdleCallbackListeners && snapshot.requestIdleCallbackListeners.forEach(item => _requestIdleCallback(item.cb));
        lighthub.requestAnimationFrameListeners = snapshot.requestIdleCallbackListeners;


        snapshot.mutationObservers && snapshot.mutationObservers.forEach(item => _mutationObserverObserve.call(item.observer, item.targetNode, item.config));
        lighthub.mutationObservers = snapshot.mutationObservers;

        applyAttrs(document.body, snapshot.bodyAttrs);

        applyAttrs(document.documentElement, snapshot.htmlAttrs);


        
        if (!isBack && snapshot.headChildren.length > 0) {
            const headtasks = [] as Promise<any>[];
            snapshot.headChildren.forEach(item => {
                headtasks.push(applyStyleSheetInDOM(item as HTMLElement, document.head));
            });

            Promise.all(headtasks).then(() => {
                
                // const hiddenStyle = document.head.querySelector('#lighthub-body-hidden');
                // hiddenStyle && hiddenStyle.remove();
                showBody && showBody();
            });

            snapshot.bodyChildren.forEach(item => {
                applyStyleSheetInDOM(item as HTMLElement, document.head);
            });
        }

        

        for (let item of Array.from(snapshot.headChildren)) {
            // @ts-ignore
            await runInQueue(() => applyDOM(item, document.head, isBack));
        }


        for (let item of Array.from(snapshot.bodyChildren)) {
            // @ts-ignore
            await runInQueue(() => applyDOM(item, document.body, isBack));
        }

        

        done && done();

        if (isBack) {
            reapplyJSONP(document.head);
        }
    }

}
